<script setup lang="ts">
import { computed, ref } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { CampaignService } from '@/services/campaign.services';
import CampaignFormPanel from './CampaignFormPanel.vue';
import { useCampaignToast } from './useCampaignToast';
import type { ICampaign, ICampaignStatus } from './campaign.types';

const { notifySuccess, notifyError } = useCampaignToast();

const props = defineProps<{ storeId: string }>();

const emit = defineEmits<{
  'edit-campaign': [id: string];
}>();

const queryClient = useQueryClient();

const {
  data: campaigns,
  isPending: isLoading,
  isError,
} = useQuery({
  queryKey: computed(() => ['campaigns-list', props.storeId]),
  queryFn: () =>
    CampaignService.list(props.storeId, {
      status: ['draft', 'active', 'paused'],
    }),
  enabled: computed(() => !!props.storeId),
});

const STATUS_LABELS: Record<ICampaignStatus, string> = {
  draft: 'Brouillon',
  active: 'Active',
  paused: 'En pause',
};
const STATUS_COLORS: Record<ICampaignStatus, string> = {
  draft: '#94a3b8',
  active: '#10b981',
  paused: '#f59e0b',
};

function fmtDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-BE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function thumbnailFor(campaign: ICampaign): string | null {
  const media =
    campaign.featuredImages.find((m) => m.role === 'thumbnail') ??
    campaign.featuredImages.find((m) => m.role === 'banner');
  return media?.imageUrl ?? null;
}

const showCreateForm = ref(false);

function openCreateForm() {
  showCreateForm.value = true;
}
function closeForm() {
  showCreateForm.value = false;
}
function onSaved(campaign: ICampaign) {
  showCreateForm.value = false;
  notifySuccess('Campagne créée avec succès.');
  emit('edit-campaign', campaign.id);
}

const pendingActionId = ref<string | null>(null);

async function publish(campaign: ICampaign) {
  pendingActionId.value = campaign.id;
  try {
    await CampaignService.publish(props.storeId, campaign.id);
    await queryClient.invalidateQueries({ queryKey: ['campaigns-list'] });
    notifySuccess('Campagne publiée.');
  } catch (err: any) {
    notifyError(err, 'Impossible de publier la campagne');
  } finally {
    pendingActionId.value = null;
  }
}

async function pause(campaign: ICampaign) {
  pendingActionId.value = campaign.id;
  try {
    await CampaignService.pause(props.storeId, campaign.id);
    await queryClient.invalidateQueries({ queryKey: ['campaigns-list'] });
    notifySuccess('Campagne mise en pause.');
  } catch (err: any) {
    notifyError(err, 'Impossible de mettre en pause');
  } finally {
    pendingActionId.value = null;
  }
}

async function remove(campaign: ICampaign) {
  const displayName = campaign.name || 'cette campagne';
  if (!confirm(`Supprimer ${displayName} ? Cette action est irréversible.`)) {
    return;
  }
  pendingActionId.value = campaign.id;
  try {
    await CampaignService.remove(props.storeId, campaign.id);
    await queryClient.invalidateQueries({ queryKey: ['campaigns-list'] });
    notifySuccess('Campagne supprimée.');
  } catch (err: any) {
    notifyError(err, 'Impossible de supprimer la campagne');
  } finally {
    pendingActionId.value = null;
  }
}
</script>

<template>
  <div class="clp">
    <CampaignFormPanel
      v-if="showCreateForm"
      :store-id="storeId"
      @saved="onSaved"
      @cancel="closeForm"
    />

    <template v-else>
      <div class="clp__header">
        <h2 class="clp__title">Menus &amp; événements</h2>
        <button class="clp__btn clp__btn--primary" @click="openCreateForm">
          <i class="pi pi-plus" /> Nouvelle campagne
        </button>
      </div>

      <div v-if="isLoading" class="clp__cards">
        <div v-for="i in 4" :key="i" class="clp__card clp__card--sk" />
      </div>
      <div v-else-if="isError" class="clp__empty">
        <i class="pi pi-exclamation-circle" />
        <p>Impossible de charger les campagnes.</p>
      </div>
      <div v-else-if="!campaigns?.length" class="clp__empty">
        <i class="pi pi-calendar" />
        <p>Aucune campagne pour ce commerce.</p>
      </div>

      <div v-else class="clp__cards">
        <div
          v-for="campaign in campaigns"
          :key="campaign.id"
          class="clp__card"
          @click="emit('edit-campaign', campaign.id)"
        >
          <div class="clp__card-media">
            <img
              v-if="thumbnailFor(campaign)"
              :src="thumbnailFor(campaign) as string"
              class="clp__thumb"
              alt=""
            />
            <div v-else class="clp__thumb clp__thumb--placeholder">
              <i class="pi pi-image" />
            </div>
          </div>

          <div class="clp__card-body">
            <div class="clp__card-hd">
              <span class="clp__name">{{ campaign.name || 'Sans nom' }}</span>
              <span
                class="clp__pill"
                :style="{
                  background: STATUS_COLORS[campaign.status] + '1a',
                  color: STATUS_COLORS[campaign.status],
                  borderColor: STATUS_COLORS[campaign.status] + '40',
                }"
              >
                {{ STATUS_LABELS[campaign.status] }}
              </span>
            </div>
            <p class="clp__dates">
              {{ fmtDate(campaign.startAt) }} → {{ fmtDate(campaign.endAt) }}
            </p>

            <div class="clp__actions" @click.stop>
              <button
                v-if="campaign.status === 'draft'"
                class="clp__action-btn"
                :disabled="pendingActionId === campaign.id"
                @click="publish(campaign)"
              >
                <i class="pi pi-send" /> Publier
              </button>
              <button
                v-if="campaign.status === 'active'"
                class="clp__action-btn"
                :disabled="pendingActionId === campaign.id"
                @click="pause(campaign)"
              >
                <i class="pi pi-pause" /> Pause
              </button>
              <button
                class="clp__action-btn clp__action-btn--danger"
                :disabled="pendingActionId === campaign.id"
                @click="remove(campaign)"
              >
                <i class="pi pi-trash" /> Supprimer
              </button>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.clp {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}
.clp__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.clp__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--nxt-on-surface);
}
.clp__btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.5rem;
  padding: 0 1.25rem;
  border-radius: 0.625rem;
  font-size: 0.875rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
  border: none;
}
.clp__btn--primary {
  background: #ffd60a;
  color: #0b1320;
}
.clp__error {
  margin: 0;
  padding: 0.75rem 1rem;
  border-radius: 0.625rem;
  background: rgba(239, 68, 68, 0.1);
  color: var(--nxt-error);
  font-size: 0.8125rem;
}
.clp__cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.25rem;
}
.clp__card {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--nxt-outline-variant);
  border-radius: 1rem;
  background: var(--nxt-card-bg);
  overflow: hidden;
  cursor: pointer;
  transition:
    box-shadow 0.2s ease,
    transform 0.2s ease;
}
.clp__card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}
.clp__card--sk {
  height: 12rem;
  cursor: default;
  background: linear-gradient(
    90deg,
    var(--nxt-surface-low) 25%,
    var(--nxt-surface-mid) 50%,
    var(--nxt-surface-low) 75%
  );
  background-size: 200% 100%;
  animation: clp-shimmer 1.4s infinite;
}
@keyframes clp-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
.clp__card-media {
  width: 100%;
  height: 8rem;
  background: var(--nxt-surface-low);
}
.clp__thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.clp__thumb--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--nxt-on-surface-muted);
  font-size: 1.5rem;
}
.clp__card-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 1rem;
}
.clp__card-hd {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}
.clp__name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.clp__pill {
  flex-shrink: 0;
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 0.2rem 0.625rem;
  border-radius: 0.4rem;
  border: 1px solid;
  white-space: nowrap;
}
.clp__dates {
  margin: 0;
  font-size: 0.75rem;
  color: var(--nxt-on-surface-muted);
}
.clp__actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.25rem;
}
.clp__action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  height: 2rem;
  padding: 0 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--nxt-outline-variant);
  background: var(--nxt-surface-low);
  color: var(--nxt-on-surface-variant);
  font-size: 0.75rem;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
}
.clp__action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.clp__action-btn--danger {
  color: var(--nxt-error);
}
.clp__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 4rem 1rem;
  color: var(--nxt-on-surface-muted);
}
.clp__empty i {
  font-size: 2rem;
  opacity: 0.4;
}
</style>
